import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useT } from "@/lib/translation";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BarChart2, Briefcase, TrendingUp, Leaf, Receipt } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import BrokerageGuideModal from "@/features/transactions/components/BrokerageGuideModal";
import TemplateHelpModal from "@/features/transactions/components/TemplateHelpModal";
import { useDownloadTemplate } from "@/features/transactions/hooks/useDownloadTemplate";
import { brokerageNameMap } from "@/features/portfolios/utils/portfolioUtils";
import {
  type Portfolio,
  getMe,
  getUserSettings,
  updateUserSettings,
  getTickerNotifications,
  ackTickerNotifications,
} from "@/api/client";
import WelcomeModal from "@/components/WelcomeModal";
import TickerAddedModal from "@/components/TickerAddedModal";

function hasTransactions(portfolios: Portfolio[]): boolean {
  return portfolios.some(
    (p) =>
      Number(p.total_value) > 0 ||
      Number(p.realized_gain_total) !== 0 ||
      Number(p.dividend_base_total) > 0 ||
      Number(p.interest_base_total) > 0,
  );
}

interface OnboardingModalsProps {
  portfolios: Portfolio[];
  isLoading: boolean;
}

export default function OnboardingModals({
  portfolios,
  isLoading,
}: OnboardingModalsProps) {
  const t = useT();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [importTriggered, setImportTriggered] = useState(false);
  const [importDismissed, setImportDismissed] = useState(false);
  const prevHasPortfolioRef = useRef<boolean | null>(null);
  const [celebrationOpen, setCelebrationOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [templateHelpOpen, setTemplateHelpOpen] = useState(false);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const { handleDownloadTemplate, isDownloadingTemplate } =
    useDownloadTemplate();
  const [pendingCelebration, setPendingCelebration] = useState(false);

  const { data: me } = useQuery({
    queryKey: ["me"],
    queryFn: getMe,
    retry: false,
  });
  const { data: userSettings, isLoading: settingsLoading } = useQuery({
    queryKey: ["userSettings"],
    queryFn: getUserSettings,
  });
  const { data: tickerNotifications } = useQuery({
    queryKey: ["tickerNotifications"],
    queryFn: getTickerNotifications,
    enabled: !!me?.id,
  });
  const celebrationShownKey = me?.id
    ? `onboarding_celebration_v2_${me.id}`
    : null;

  const hasPortfolio = portfolios.length > 0;
  const hasData = hasTransactions(portfolios);
  const brokerage = portfolios[0]?.brokerage ?? null;
  const brokerageName = brokerage
    ? (brokerageNameMap[brokerage] ?? brokerage)
    : null;

  // Phase 0: Show concept modal once per user, regardless of portfolio count
  useEffect(() => {
    if (isLoading || settingsLoading || !userSettings) return;
    if (!userSettings.onboarding_welcome_seen) {
      setWelcomeOpen(true);
      updateUserSettings({ onboarding_welcome_seen: true }).catch(() => {});
      queryClient.setQueryData(["userSettings"], {
        ...userSettings,
        onboarding_welcome_seen: true,
      });
    }
  }, [isLoading, settingsLoading, userSettings, queryClient]);

  // Trigger import modal only when portfolio is created during this session (not on login with existing portfolio)
  useEffect(() => {
    if (isLoading) return;
    if (
      prevHasPortfolioRef.current === false &&
      hasPortfolio &&
      !hasData &&
      !importTriggered
    ) {
      setImportTriggered(true);
    }
    prevHasPortfolioRef.current = hasPortfolio;
  }, [isLoading, hasPortfolio, hasData, importTriggered]);

  // Track the import modal's live open/closed state so the celebration
  // modal can be hard-blocked from rendering while it's still open — this
  // is independent of (and a backstop for) the pendingCelebration flag
  // below, which only tells us a celebration is *owed*, not that it's
  // currently safe to show.
  useEffect(() => {
    const handler = (event: Event) => {
      const open =
        (event as CustomEvent<{ open?: boolean }>).detail?.open ?? false;
      setImportModalOpen(open);
    };
    window.addEventListener(
      "portfolio-tracker:import-modal-open-state",
      handler,
    );
    return () =>
      window.removeEventListener(
        "portfolio-tracker:import-modal-open-state",
        handler,
      );
  }, []);

  // Set pending flag when import modal closes after a completed import
  // (portfolios may not have refetched yet). Ignore closes where the user
  // dismissed the modal without ever seeing an upload result — otherwise a
  // stale flag can fire the celebration on top of a later, still-open
  // results screen.
  useEffect(() => {
    const handler = (event: Event) => {
      const hadResults =
        (event as CustomEvent<{ hadResults?: boolean }>).detail?.hadResults ??
        false;
      if (
        hadResults &&
        (!celebrationShownKey || !localStorage.getItem(celebrationShownKey))
      ) {
        setPendingCelebration(true);
      }
    };
    window.addEventListener("portfolio-tracker:import-modal-closed", handler);
    return () =>
      window.removeEventListener(
        "portfolio-tracker:import-modal-closed",
        handler,
      );
  }, [celebrationShownKey]);

  // Open celebration once portfolios have refetched and confirmed data
  // exists — but never while the import modal is still on screen, even if
  // pendingCelebration and hasData are already both true.
  useEffect(() => {
    if (
      pendingCelebration &&
      hasData &&
      celebrationShownKey &&
      !importModalOpen
    ) {
      localStorage.setItem(celebrationShownKey, "true");
      setCelebrationOpen(true);
      setPendingCelebration(false);
    }
  }, [pendingCelebration, hasData, celebrationShownKey, importModalOpen]);

  const showImportModal = importTriggered && !hasData && !importDismissed;

  const handleAcknowledgeTickerNotifications = () => {
    if (!tickerNotifications || tickerNotifications.length === 0) return;
    const ids = tickerNotifications.map((n) => n.id);
    queryClient.setQueryData(["tickerNotifications"], []);
    ackTickerNotifications(ids).catch(() => {
      queryClient.invalidateQueries({ queryKey: ["tickerNotifications"] });
    });
  };

  return (
    <>
      {/* Phase 0: Concept onboarding */}
      <WelcomeModal open={welcomeOpen} onClose={() => setWelcomeOpen(false)} />

      {/* Newly added tickers the user requested */}
      <TickerAddedModal
        open={!!tickerNotifications && tickerNotifications.length > 0}
        notifications={tickerNotifications ?? []}
        onAcknowledge={handleAcknowledgeTickerNotifications}
      />

      {/* Phase 2: Import transactions */}
      <Dialog
        open={showImportModal}
        onOpenChange={(open) => {
          if (!open) setImportDismissed(true);
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl">
              {t("onboarding:import.title")}
            </DialogTitle>
            <DialogDescription className="text-base">
              {t("onboarding:import.subtitle")}
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-lg border bg-muted/40 px-4 py-4 space-y-3">
            <p className="text-sm font-medium">
              {t("onboarding:import.whatHappens")}
            </p>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex gap-2">
                <span className="text-foreground">—</span>{" "}
                {t("onboarding:import.benefitNetWorth")}
              </li>
              <li className="flex gap-2">
                <span className="text-foreground">—</span>{" "}
                {t("onboarding:import.benefitPerformance")}
              </li>
              <li className="flex gap-2">
                <span className="text-foreground">—</span>{" "}
                {t("onboarding:import.benefitDividends")}
              </li>
            </ul>
          </div>

          <div className="flex flex-col gap-2 pt-1">
            <Button
              onClick={() => {
                setImportDismissed(true);
                navigate("/portfolio/transactions");
              }}
              className="w-full"
            >
              {t("onboarding:import.goToTransactions")}
            </Button>
            {brokerage === "manual" ? (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setTemplateHelpOpen(true)}
              >
                {t("onboarding:import.csvTemplateHelp")}
              </Button>
            ) : (
              brokerageName && (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setGuideOpen(true)}
                >
                  {t("onboarding:import.exportHelp", {
                    brokerage: brokerageName,
                  })}
                </Button>
              )
            )}
            <Button
              variant="ghost"
              className="w-full text-muted-foreground"
              onClick={() => setImportDismissed(true)}
            >
              {t("onboarding:import.later")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Phase 3: First import celebration */}
      <Dialog open={celebrationOpen} onOpenChange={setCelebrationOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl">
              {t("onboarding:celebration.title")}
            </DialogTitle>
            <DialogDescription className="text-base">
              {t("onboarding:celebration.subtitle")}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3 py-1">
            {FEATURES.map(({ icon: Icon, titleKey, descriptionKey }) => (
              <div
                key={titleKey}
                className="rounded-lg border bg-muted/30 p-4 space-y-1.5"
              >
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4 text-primary shrink-0" />
                  <span className="text-sm font-semibold">{t(titleKey)}</span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {t(descriptionKey)}
                </p>
              </div>
            ))}
          </div>

          <div className="flex gap-2 pt-1">
            <Button
              className="flex-1"
              onClick={() => {
                setCelebrationOpen(false);
                navigate("/portfolio/dashboard");
              }}
            >
              {t("onboarding:celebration.goToDashboard")}
            </Button>
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setCelebrationOpen(false)}
            >
              {t("onboarding:celebration.explore")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <BrokerageGuideModal
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
        brokerage={brokerage}
      />
      <TemplateHelpModal
        isOpen={templateHelpOpen}
        onOpenChange={setTemplateHelpOpen}
        onDownloadTemplate={handleDownloadTemplate}
        isDownloadingTemplate={isDownloadingTemplate}
      />
    </>
  );
}

const FEATURES = [
  {
    icon: BarChart2,
    titleKey: "onboarding:celebration.features.dashboard.title",
    descriptionKey: "onboarding:celebration.features.dashboard.description",
  },
  {
    icon: Briefcase,
    titleKey: "onboarding:celebration.features.holdings.title",
    descriptionKey: "onboarding:celebration.features.holdings.description",
  },
  {
    icon: TrendingUp,
    titleKey: "onboarding:celebration.features.performance.title",
    descriptionKey: "onboarding:celebration.features.performance.description",
  },
  {
    icon: Leaf,
    titleKey: "onboarding:celebration.features.dividends.title",
    descriptionKey: "onboarding:celebration.features.dividends.description",
  },
  {
    icon: Receipt,
    titleKey: "onboarding:celebration.features.taxCalculator.title",
    descriptionKey: "onboarding:celebration.features.taxCalculator.description",
  },
] as const;
